import { UiSectionContentComponent } from '@/ui/section/section-content/ui-section-content';
import { UiSectionComponent } from '@/ui/section/ui-section';
import { isNotNullish } from '@/utils/typing/is-nullish.predicate';
import { Component, inject, input, ChangeDetectionStrategy } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { map } from 'rxjs';
import { LoadingAndErrorComponent } from '../../../../../components/loading-and-error/loading-and-error';
import { LoadingAndErrorResourceBindingDirective } from '../../../../../components/loading-and-error/loading-and-error-resource-binding.directive';
import type { BusinessIndicatorId } from '../../../../../interfaces/BusinessIndicator.interface';
import type { ClassificationNodeId } from '../../../../../interfaces/Classification.interface';
import type { UnitCode } from '../../../../../interfaces/Unit.interface';
import { HighchartsStaticProvider } from '../../../../../providers/highcharts/highcharts.static.provider';
import { HighchartsChartComponent } from './../../../../../components/highcharts-chart/highcharts-chart';
import { type BusinessIndicatorAdvancementConfig, BusinessIndicatorsService } from './../../services/business-indicators.service';

@Component({
    changeDetection: ChangeDetectionStrategy.Eager,
    selector: 'widget-business-indicators-advancement',
    imports: [
        UiSectionComponent,
        TranslatePipe,
        UiSectionContentComponent,
        HighchartsChartComponent,
        LoadingAndErrorComponent,
        LoadingAndErrorResourceBindingDirective
    ],
    templateUrl: './widget-business-indicators-advancement.component.html',
    styleUrl: './widget-business-indicators-advancement.component.scss'
})
export class WidgetBusinessIndicatorsAdvancementComponent {
    private _businessIndicatorsService = inject(BusinessIndicatorsService);
    private _highchartsStaticProvider = inject(HighchartsStaticProvider);
    private _translateService = inject(TranslateService);

    businessIndicatorId = input.required<BusinessIndicatorId>();
    classificationNodeId = input.required<ClassificationNodeId>();
    isEstimated = input.required<boolean>();
    title = input<string>();
    displayLegend = input<boolean>();

    chartConfig = rxResource({
        params: () => ({
            businessIndicatorId: this.businessIndicatorId(),
            classificationNodeId: this.classificationNodeId(),
            isEstimated: this.isEstimated(),
            displayLegend: this.displayLegend()
        }),
        stream: ({ params }) => {
            return this._businessIndicatorsService
                .fetchBusinessIndicatorYearlyTimeseries$(params.businessIndicatorId, params.classificationNodeId)
                .pipe(
                    map(businessIndicatorAdvancementConfig => {
                        return this.generateHighchartConfig(
                            this.generateSeries(businessIndicatorAdvancementConfig, params.isEstimated),
                            businessIndicatorAdvancementConfig.unitCode,
                            params.displayLegend
                        );
                    })
                );
        }
    });

    generateHighchartConfig(series: Highcharts.SeriesOptionsType[], unit: UnitCode, displayLegend: boolean = true): Highcharts.Options {
        return this._highchartsStaticProvider.buildConfig(this._highchartsStaticProvider.COLUMN_MIN_SIZE_5, {
            xAxis: {
                type: 'category'
            },
            yAxis: {
                labels: {
                    format: `{value} ${unit}`
                }
            },
            tooltip: {
                shared: true,
                valueDecimals: 0,
                valueSuffix: ` ${unit}`
            },
            plotOptions: {
                column: {
                    stacking: 'normal'
                },
                spline: {
                    marker: {
                        enabled: true,
                        symbol: 'circle',
                        radius: 4
                    }
                }
            },
            legend: {
                enabled: isNotNullish(displayLegend) ? displayLegend : true
            },
            series
        });
    }

    generateSeries(
        businessIndicatorAdvancementConfig: BusinessIndicatorAdvancementConfig,
        isEstimated: boolean
    ): Highcharts.SeriesOptionsType[] {
        const series: Highcharts.SeriesOptionsType[] = [];

        // referenceSeries
        if (businessIndicatorAdvancementConfig.staticReference) {
            series.push({
                name: this._translateService.instant('PAGES.BUSINESS_INDICATORS.WIDGETS.ADVANCEMENT.SERIES.REFERENCE'),
                type: 'column',
                color: '#E86F5A',
                data: [
                    {
                        name: this._translateService.instant('PAGES.BUSINESS_INDICATORS.WIDGETS.ADVANCEMENT.SERIES.REFERENCE'),
                        y: businessIndicatorAdvancementConfig.staticReference
                    }
                ]
            });
        } else {
            series.push({
                name: this._translateService.instant('PAGES.BUSINESS_INDICATORS.WIDGETS.ADVANCEMENT.SERIES.REFERENCE'),
                type: 'spline',
                color: '#E86F5A',
                data: businessIndicatorAdvancementConfig.timeseries.map(biac => {
                    return {
                        name: String(biac.year),
                        y: biac.dynamicReference
                    };
                })
            });
        }

        // measuredSeries
        series.push({
            name: this._translateService.instant('PAGES.BUSINESS_INDICATORS.WIDGETS.ADVANCEMENT.SERIES.MEASURED'),
            type: 'column',
            color: '#A2CDC5',
            data: businessIndicatorAdvancementConfig.timeseries.map(bisy => {
                return {
                    name: String(bisy.year),
                    y: bisy.measured
                };
            }),
            index: 1
        });

        // estimatedSeries
        if (isEstimated) {
            series.push({
                name: this._translateService.instant('PAGES.BUSINESS_INDICATORS.WIDGETS.ADVANCEMENT.SERIES.ESTIMATED'),
                type: 'column',
                color: this._highchartsStaticProvider.generateDiagonalStripesPattern('#A2CDC5'),
                data: businessIndicatorAdvancementConfig.timeseries.map(bisy => {
                    return {
                        name: String(bisy.year),
                        y: bisy.estimated
                    };
                }),
                index: 0
            });
        }

        // objectiveSeries
        series.push({
            name: this._translateService.instant('PAGES.BUSINESS_INDICATORS.WIDGETS.ADVANCEMENT.SERIES.OBJECTIVE'),
            type: 'column',
            color: '#A5BFFF',
            stack: 'objective',
            data: businessIndicatorAdvancementConfig.timeseries.map(bisy => {
                return {
                    name: String(bisy.year),
                    y: bisy.objective
                };
            })
        });

        // targetSeries
        series.push({
            name: this._translateService.instant('PAGES.BUSINESS_INDICATORS.WIDGETS.ADVANCEMENT.SERIES.TARGET'),
            color: ' #A5A5A5',
            type: 'spline',
            data: businessIndicatorAdvancementConfig.timeseries.map(bisy => {
                return {
                    name: String(bisy.year),
                    y: bisy.target
                };
            })
        });

        return series;
    }

    protected readonly isNotNullish = isNotNullish;
}
